import React, { useState, useRef, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import { useFetchGet } from "hooks/fetch/useFetchGet";

import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import CloseIcon from "@mui/icons-material/Close";
import SelectedFilters from "components/CasesTable/SelectedFilters";
import { Autocomplete } from "components/Autocomplete";
import { AttributeFilterField } from "components/CasesTable/MoreFilter/AttributeFilterField";
import { MachineStatusFilter } from "components/CasesTable/Filters/MachineStatusFilter";
import { ManufacturerFilter } from "components/CasesTable/Filters/ManufacturerFilter";
import { MachineModelFilter } from "components/CasesTable/Filters/MachineModelFilter";
import { CaseActionTypeFilter } from "components/CasesTable/Filters/CaseActionTypeFilter";
import { UserFilter } from "components/CasesTable/Filters/UserFilter";
import { SeverityTypeFilter } from "components/CasesTable/Filters/SeverityTypesFilter";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@mui/material";
export function MoreFilterDialog({
  openDialog,
  handleClose,
  dialogOnClose,
  caseTableFilters,
  onSetFilters,
  clearAllFilters
}) {
  const {
    loading: attributeLoading,
    error: attributeError,
    response: attributeResponse
  } = useFetchGet("case_meta_types_expanded");
  const [temporaryFilters, setTemporaryFilters] = useState({});
  const [selectedFilter, setSelectedFilter] = useState();

  useEffect(() => {
    if (openDialog) {
      setTemporaryFilters({ ...caseTableFilters });
    } else {
      setSelectedFilter(null);
    }
  }, [openDialog, caseTableFilters]);

  const itemsRef = useRef([]);

  useEffect(() => {
    if (selectedFilter && (selectedFilter?.id || selectedFilter?.name)) {
      itemsRef.current[
        selectedFilter.id || selectedFilter?.name
      ].scrollIntoView();
    }
  }, [selectedFilter]);

  const handleTemporaryFilter = useCallback((values, title) => {
    /*
     * filter_structure = {
     *  'title_of_the_filter_group': [
     *    { id: id_by_API, ids: id_group, name: filter_value_name, value: custom_attribute_data }
     *  ]
     * }
     * */

    // Add filter group data to store
    if (typeof values === "string" || values?.length > 0) {
      const formattedValue = Array.isArray(values)
        ? values
        : [{ type_name: title, name: values }];

      // Set filter values by the group title
      setTemporaryFilters(state => {
        const formattedCurrent = { ...state };
        if (formattedCurrent[title]) {
          let isRangeFilter = false;
          let targetIndex = -1;
          formattedCurrent[title] = [];

          formattedValue.forEach(newVal => {
            if (
              ["__gte", "__lte"].some(el => newVal?.type_name?.includes(el))
            ) {
              isRangeFilter = true;
              targetIndex = formattedCurrent[title].findIndex(
                el => el.type_name === newVal.type_name
              );

              if (targetIndex > -1) {
                [targetIndex] = newVal;
              }
            }

            formattedCurrent[title].push(newVal);
          });

          // When one of range filter value empty, we have to remove out the empty filter manually
          if (
            isRangeFilter &&
            formattedCurrent[title].length > 0 &&
            formattedCurrent[title].length > formattedValue.length
          ) {
            formattedCurrent[title] = formattedCurrent[title].filter(filter =>
              formattedValue.some(value => value.name === filter.name)
            );
          }
        } else {
          formattedCurrent[title] = formattedValue;
        }
        return formattedCurrent;
      });
    } else {
      setTemporaryFilters(state => {
        const formattedCurrent = { ...state };
        delete formattedCurrent[title];
        return formattedCurrent;
      });
    }
  }, []);

  const onDeleteStore = useCallback((value, title) => {
    setTemporaryFilters(state => {
      let current = { ...state };

      // If filter group value is empty, remove the group from the store or filter values
      if (current[title]) {
        current[title] = current[title].filter(el => {
          if (el?.type_name) {
            return el.type_name !== value.type_name;
          }
          return el.name !== value.name;
        });
        if (current[title].length === 0) delete current[title];
      }
      return current;
    });
  }, []);

  const onClearStore = () => {
    setTemporaryFilters({});
  };

  const doApplyFilters = useCallback(() => {
    onSetFilters(temporaryFilters);
    setTemporaryFilters({});
    handleClose();
  }, [temporaryFilters, handleClose, onSetFilters]);

  const filterList = [
    {
      name: "Manufacturer",
      filterComponent: (
        <ManufacturerFilter
          selectedManufacturers={temporaryFilters["Manufacturer"] ?? []}
          onChangeSelectedManufacturers={values =>
            handleTemporaryFilter(values, "Manufacturer")
          }
          showChips={false}
        />
      )
    },
    {
      name: "Model",
      filterComponent: (
        <MachineModelFilter
          selectedMachineModels={temporaryFilters["Model"] ?? []}
          onChangeSelectedMachineModels={values =>
            handleTemporaryFilter(values, "Model")
          }
          showChips={false}
        />
      )
    },
    {
      name: "Machine Status",
      filterComponent: (
        <MachineStatusFilter
          selectedMachineStatuses={temporaryFilters["Machine Status"] ?? []}
          onChangeSelectedMachineStatuses={values =>
            handleTemporaryFilter(values, "Machine Status")
          }
          showChips={false}
        />
      )
    },
    {
      name: "Next Action",
      filterComponent: (
        <CaseActionTypeFilter
          selectedCaseActions={temporaryFilters["Next Action"] || []}
          onChangeSelectedCaseActions={values =>
            handleTemporaryFilter(values, "Next Action")
          }
          showChips={false}
        />
      )
    },
    {
      name: "Created By",
      filterComponent: (
        <UserFilter
          title="Created by"
          selectedUsers={temporaryFilters["Created by"] || []}
          onChangeSelectedUsers={values =>
            handleTemporaryFilter(values, "Created by")
          }
          showChips={false}
          emptyOption="unassigned"
        />
      )
    },
    {
      name: "Diagnostic",
      filterComponent: (
        <SeverityTypeFilter
          title="Diagnostic"
          selectedSeverities={temporaryFilters["Diagnostic"] || []}
          onChangeSelectedSeverities={values =>
            handleTemporaryFilter(values, "Diagnostic")
          }
          showChips={false}
        />
      )
    }
  ];

  return (
    <Dialog open={openDialog} onClose={dialogOnClose} fullWidth>
      <DialogTitle>
        <AlignedTitle>
          More Filters
          <IconButton onClick={handleClose} size="large">
            <CloseIcon />
          </IconButton>
        </AlignedTitle>
      </DialogTitle>

      {attributeResponse?.results ? (
        <DialogContent sx={{ overflow: "visible" }}>
          <Autocomplete
            loading={attributeLoading}
            onChange={(event, filter) => {
              setSelectedFilter(filter);
            }}
            label="Find Filter"
            options={[
              ...filterList.map(filter => ({ name: filter.name })),
              ...attributeResponse.results
            ]}
            variant={"outlined"}
            getOptionLabel={option => option.name}
            size="small"
          />
        </DialogContent>
      ) : null}

      <DialogContent>
        {filterList.map(({ name, filterComponent }, filterIndex) => (
          <div ref={el => (itemsRef.current[name] = el)} key={name}>
            <InputWrapper selected={name === selectedFilter?.name}>
              {filterComponent}
            </InputWrapper>

            {filterIndex + 1 !== filterList.length ? (
              <DividerWithMargin variant="middle" />
            ) : null}
          </div>
        ))}

        {attributeResponse?.results?.length &&
        !attributeLoading &&
        !attributeError
          ? attributeResponse.results.map(attr => (
              <div ref={el => (itemsRef.current[attr.id] = el)} key={attr.id}>
                <DividerWithMargin variant="middle" />
                <InputWrapper selected={attr.id === selectedFilter?.id}>
                  <AttributeFilterField
                    attributeData={attr}
                    filters={temporaryFilters}
                    setFieldValue={handleTemporaryFilter}
                  />
                </InputWrapper>
              </div>
            ))
          : null}
      </DialogContent>

      <ActionWrapper>
        <SelectedFilters
          selectedFilterValues={temporaryFilters}
          onSetFilters={handleTemporaryFilter}
          onClearStore={onClearStore}
          enableClear={true}
          clearAllFilters={clearAllFilters}
          onDeleteStore={onDeleteStore}
        />

        <AlignedActionButtonWrap>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={doApplyFilters}>
            Apply
          </Button>
        </AlignedActionButtonWrap>
      </ActionWrapper>
    </Dialog>
  );
}

const AlignedTitle = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
AlignedTitle.displayName = "AlignedTitle";

const InputWrapper = styled("div")`
  ${props =>
    props.selected &&
    `
    .MuiInputBase-root {
      background-color: ${props.theme.palette.action.selected};
    }
  `}
`;
InputWrapper.displayName = "InputWrapper";

const ActionWrapper = styled(DialogActions)`
  display: flex;
  flex-wrap: wrap;
  padding: 0 ${props => props.theme.spacing(2)};
`;
ActionWrapper.displayName = "ActionWrapper";

const AlignedActionButtonWrap = styled("div")`
  display: flex;
  width: 100%;
  justify-content: right;
  gap: 8px;
  margin: 8px 0 0;
  padding: 0;
  padding-top: ${props => props.theme.spacing(1)};
  padding-bottom: ${props => props.theme.spacing(2)};
`;
AlignedActionButtonWrap.displayName = "AlignedActionButtonWrap";

const DividerWithMargin = styled(Divider)`
  margin: ${({ theme }) => theme.spacing(1.5)} 0;
`;
DividerWithMargin.displayName = "DividerWithMargin";

MoreFilterDialog.propTypes = {
  openDialog: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  dialogOnClose: PropTypes.func.isRequired,
  caseTableFilters: PropTypes.object,
  onSetFilters: PropTypes.func,
  clearAllFilters: PropTypes.func
};
